import { AlertCircle, ChevronRight } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  describeRequestItems,
  getRequestStatus,
  REQUEST_STATUS_BADGE_CLASSNAMES,
  type RequestListEntry,
  type RequestStatus,
} from "./supportRequest";

const DIRECT_CANCELLATION_STATUS: RequestStatus = { label: "Cancelado", tone: "resolved" };

interface RowContent {
  key: string;
  title: string;
  date: string | null;
  isWholeOrder: boolean;
  item: string | null;
  status: RequestStatus;
}

function toRowContent(entry: RequestListEntry): RowContent {
  if (entry.kind === "directCancellation") {
    return {
      key: "direct-cancellation",
      title: entry.title,
      date: entry.cancelledAt,
      isWholeOrder: true,
      item: null,
      status: DIRECT_CANCELLATION_STATUS,
    };
  }
  const { request } = entry;
  // Itens do chamado (protótipo 1d: "High Five - Material · 1 un." / "· −1 un."); sem itens = pedido inteiro.
  const items = describeRequestItems(request);
  return {
    key: String(request.id),
    title: request.title,
    date: request.createdAt,
    isWholeOrder: items === null,
    item: items,
    status: getRequestStatus(request.ticketStatus),
  };
}

// `span` com o visual do Badge: a linha é um `button`, que só aceita conteúdo inline.
export const RequestStatusBadge = ({ status, className }: { status: RequestStatus; className?: string }) => (
  <span
    className={cn(
      badgeVariants({ variant: "outline" }),
      "whitespace-nowrap px-3 py-1",
      REQUEST_STATUS_BADGE_CLASSNAMES[status.tone],
      className
    )}
  >
    {status.label}
  </span>
);

interface OrderRequestsListProps {
  entries: RequestListEntry[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
  onSelect: (entry: RequestListEntry) => void;
}

/**
 * "Solicitações do pedido": cancelamento (pedido inteiro), trocas, checklists e o que mais existir, cada um com
 * o próprio ticket. Duas solicitações podem ter o mesmo título, então o item (descrição e quantidade) diferencia.
 */
export const OrderRequestsList = ({ entries, isLoading, isError, isFetching, onRetry, onSelect }: OrderRequestsListProps) => (
  <div className="flex flex-col gap-5 p-7">
    <div className="flex flex-col gap-1">
      <h2 className="text-[19px] font-bold leading-tight">Solicitações do pedido</h2>
    </div>

    {isLoading ? (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Carregando solicitações">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-[74px] w-full rounded-[10px]" />
        ))}
      </div>
    ) : isError ? (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="flex flex-col items-start gap-3">
          <p>Não foi possível carregar as solicitações deste pedido.</p>
          <Button variant="outline" size="sm" onClick={onRetry} disabled={isFetching}>
            Tentar novamente
          </Button>
        </AlertDescription>
      </Alert>
    ) : entries.length === 0 ? (
      <p className="rounded-[10px] border border-dashed px-6 py-8 text-center text-sm text-muted-foreground">
        Nenhuma solicitação registrada para este pedido.
      </p>
    ) : (
      <ul className="flex flex-col gap-3">
        {entries.map((entry) => {
          const row = toRowContent(entry);
          const date = row.date ? formatDate(new Date(row.date)) : "—";
          return (
            <li key={row.key}>
              <button
                type="button"
                onClick={() => onSelect(entry)}
                className="grid w-full grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 rounded-[10px] border bg-muted/30 px-5 py-4 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-[1.6fr_1.4fr_1fr_20px]"
              >
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="text-[15px] font-semibold leading-snug">{row.title}</span>
                  <span className="text-[13px] text-muted-foreground">
                    {row.isWholeOrder ? `Pedido inteiro · ${date}` : date}
                  </span>
                </span>
                <span className="col-span-2 row-start-2 min-w-0 truncate text-[13px] text-muted-foreground sm:col-span-1 sm:row-start-auto">
                  {row.item ?? "—"}
                </span>
                <span className="col-start-2 row-start-1 justify-self-end sm:col-start-auto sm:row-start-auto sm:justify-self-start">
                  <RequestStatusBadge status={row.status} />
                </span>
                <ChevronRight className="hidden h-5 w-5 text-muted-foreground sm:block" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);
